<template>
  <div>
    <InputText
      v-model="groupName"
      placeholder="Group Name"
      class="mb-2"
      fluid
    />
    <div class="mb-4">
      <div class="mb-4">
        <label
          for="color-left"
          class="block mb-2 font-medium"
        >
          Colour 1: {{ hexToColorName(selectedColorLeft) }} ({{ selectedColorLeft }})
        </label>
        <ColorPicker 
          v-model="selectedColorLeft"
          input-id="color-left"
          format="hex"
          inline
        />
      </div>
      <div>
        <label
          for="color-right"
          class="block mb-2 font-medium"
        >
          Colour 2: {{ hexToColorName(selectedColorRight) }} ({{ selectedColorRight }})
        </label>
        <ColorPicker 
          v-model="selectedColorRight"
          input-id="color-right"
          format="hex"
          inline
        />
      </div>
    </div>
    <!-- Save Button -->
    <Button 
      :label="isSubmitting ? 'Saving...' : 'Save Group'" 
      class="mt-2 w-full" 
      :loading="isSubmitting" 
      :disabled="!groupName.trim() || isSubmitting"
      severity="primary"
      icon="pi pi-check"
      @click="saveGroup"
    />
  </div>
</template>

<script setup>
import { ref } from "vue";
import { addEquipmentListContent } from '@/services/projectService';
import { projectStore } from "../projectStore";
import { useToastService } from '@/composables/useToastService';

// Toast service
const toast = useToastService();

const groupName = ref("");
const selectedColorLeft = ref("#ff0000"); // Default red in hex
const selectedColorRight = ref("#0000ff"); // Default blue in hex
const isSubmitting = ref(false);

// Function to convert hex color to nearest color name
const hexToColorName = (hex) => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Convert hex to RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Define color ranges for each named color
    const colors = [
        { name: 'red', r: 255, g: 0, b: 0 },
        { name: 'green', r: 0, g: 255, b: 0 },
        { name: 'blue', r: 0, g: 0, b: 255 },
        { name: 'orange', r: 255, g: 165, b: 0 },
        { name: 'yellow', r: 255, g: 255, b: 0 },
        { name: 'purple', r: 128, g: 0, b: 128 },
        { name: 'black', r: 0, g: 0, b: 0 },
        { name: 'grey', r: 128, g: 128, b: 128 },
        { name: 'brown', r: 165, g: 42, b: 42 }
    ];
    
    // Find the closest color using Euclidean distance
    let minDistance = Infinity;
    let closestColor = 'grey';
    
    colors.forEach(color => {
        const distance = Math.sqrt(
            Math.pow(r - color.r, 2) +
            Math.pow(g - color.g, 2) +
            Math.pow(b - color.b, 2)
        );
        
        if (distance < minDistance) {
            minDistance = distance;
            closestColor = color.name;
        }
    });
    
    return closestColor;
};

const saveGroup = async () => {
    // Prevent multiple submissions
    if (!groupName.value.trim() || isSubmitting.value) {
        return;
    }

    isSubmitting.value = true;

    try {
        const projectId = projectStore.currentProjectId;
        const equipmentListId = projectStore.currentEquipmentListId;
        
        if (!projectId || !equipmentListId) {
            throw new Error('Project ID or Equipment List ID not found');
        }

        const contentData = {
            type: "group",
            name: groupName.value.trim(),
            icon_colour_left: hexToColorName(selectedColorLeft.value),
            icon_colour_right: hexToColorName(selectedColorRight.value)
        };

        // Add the group via API
        await addEquipmentListContent(projectId, equipmentListId, contentData);

        // Refresh the equipment list content to show the new group
        if (projectStore.refreshFunction) {
            await projectStore.refreshFunction();
        }

        // Show success toast using PrimeVue toast service
        toast.add({
            severity: 'success',
            summary: 'Group Added',
            detail: `Group "${groupName.value.trim()}" added successfully`,
            life: 3000
        });

        // Close the modal and reset form
        projectStore.closeModal();
        resetForm();

    } catch (error) {
        console.error('Error adding group:', error);
        
        // Show error toast using PrimeVue toast service
        toast.add({
            severity: 'error',
            summary: 'Error Adding Group',
            detail: error.response?.data?.message || error.message || 'Failed to add group. Please try again.',
            life: 5000
        });
    } finally {
        // Reset submitting state after a delay to prevent rapid clicking
        setTimeout(() => {
            isSubmitting.value = false;
        }, 1000);
    }
};

// Function to reset the form
const resetForm = () => {
    groupName.value = "";
    selectedColorLeft.value = "#ff0000"; // Red
    selectedColorRight.value = "#0000ff"; // Blue
    isSubmitting.value = false;
};
</script>

<style scoped>
/* Style inline color pickers */
:deep(.p-colorpicker-panel) {
    box-shadow: none;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
}
</style>
